import {
  GridActionsCellItem,
  GridColDef,
  GridColumns,
  GridRenderCellParams,
  GridRowParams
} from '@mui/x-data-grid-premium';
import FileUploader from 'components/common/file-uploader';
import {
  isTenantAdministrator,
  IsTenantAdministrator
} from 'components/helpers/IsTenantAdministrator';
import {
  filterOperators,
  filterListOperators,
  dateFilterOperators,
  booleanFilterOperators,
  numberFilterOperators
} from './../../../../constants';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { equipmentService } from 'services/api';
import { IsSuperAdminGuard } from 'components/helpers/IsSuperAdmin';
import { Chip } from '@mui/material';
import { Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Check, Close } from '@mui/icons-material';
import equipmentStatusCell from '../cells/equipment-status-cell';
import { currencyCell } from '../cells/currency-cell';
import { booleanValueFormatter } from '../data-grid/utils';
import DataGridCell from 'components/common/DataGridCell';
import ConfirmationUnlinkTableDialog from 'components/helpers/ConfirmationGridUnlink';
import listCell from '../cells/list-cell';
import { isDirector } from 'components/helpers/IsDirector';

const equipmentOptions = ['NEW', 'REFURBISHED', 'DECOMMISSIONED'];

export const EquipmentColumns = (
  onUploadSuccess?: any,
  detailNav = false,
  handleOpen?: (row: any) => void
): GridColDef[] => {
  const translationPrefix = 'entities.equipment';
  const { t } = useTranslation();
  const isDir = isDirector();
  const viewReport = isDirector() || isTenantAdministrator();
  const isSuperAdmin = IsSuperAdminGuard();

  const columns: GridColDef[] | GridColumns = [
    {
      field: 'code',
      headerName: t(`${translationPrefix}.code`),
      width: 200,
      filterOperators,
      renderCell: (params: GridRenderCellParams) => {
        const workstation = params.row;
        return detailNav ? (
          <div>
            <Link to={`${workstation.id}`}>
              <b className="text-secondary-500">{workstation.code}</b>
            </Link>
          </div>
        ) : (
          <DataGridCell content={params.value as string} />
        );
      }
    },
    {
      field: 'description',
      filterOperators,
      width: 250,
      headerName: t(`${translationPrefix}.description`)
    }
  ];
  if (!isDir) {
    columns.push(
      ...(isSuperAdmin
        ? [
            {
              field: 'association',
              headerName: t(`global.association`),
              width: 80,
              sortable: false,
              filterOperators: booleanFilterOperators,
              renderCell: (params: GridRenderCellParams) => (
                <div>
                  <Chip
                    sx={{ background: 'transparent' }}
                    icon={params.value ? <Check color="success" /> : <Close color="error" />}
                  />
                </div>
              )
            }
          ]
        : []),
      {
        field: 'status',
        type: 'singleSelect',
        valueOptions: equipmentOptions,
        headerName: t(`${translationPrefix}.status`),
        width: 100,
        renderCell: equipmentStatusCell(t)
      }
    );
  }
  columns.push(
    {
      field: 'ivdr_class_txt',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.ivdr_class`)
    },
    {
      field: 'serial_number',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.serial_number`)
    },
    {
      field: 'internal_id',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.internal_id`)
    },
    {
      field: 'transport_document_ref',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.transport_document_ref`)
    },
    {
      field: 'supplier',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.supplier`)
    }
  );
  if (!isDir) {
    columns.push(
      {
        field: 'warehouse_code',
        filterOperators,
        width: 150,
        headerName: t(`global.warehouse`)
      },
      {
        field: 'position',
        filterOperators,
        width: 150,
        headerName: t(`${translationPrefix}.position`)
      },
      {
        field: 'market_value',
        filterOperators: numberFilterOperators,
        width: 150,
        headerName: t(`${translationPrefix}.market_value`),
        renderCell: currencyCell
      }
    );
  }
  columns.push({
    field: 'manufacturer',
    filterOperators,
    width: 150,
    headerName: t(`${translationPrefix}.manufacturer`)
  });
  if (!isDir) {
    columns.push(
      {
        field: 'yearly_lease_amount',
        headerName: t(`entities.contract-equipment.yearly_lease_amount`),
        width: 240,
        sortable: false,
        renderCell: currencyCell
      },
      {
        field: 'yearly_maintenance_amount',
        headerName: t(`entities.contract-equipment.yearly_maintenance_amount`),
        sortable: false,
        width: 240,
        renderCell: currencyCell
      },
      {
        field: 'purchase_amount',
        headerName: t(`entities.contract-equipment.purchase_amount`),
        sortable: false,
        width: 240,
        renderCell: currencyCell
      },
      {
        field: 'accessories',
        filterOperators,
        width: 150,
        headerName: t(`${translationPrefix}.accessories`)
      },
      {
        field: 'contracts_code',
        sortable: false,
        filterOperators: filterListOperators,
        width: 200,
        renderCell: listCell,
        headerName: t(`global.contracts`)
      }
    );
  }
  columns.push(
    {
      field: 'delivery_date',
      filterOperators: dateFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.delivery_date`)
    },
    {
      field: 'installation_date',
      width: 150,
      filterOperators: dateFilterOperators,
      headerName: t(`${translationPrefix}.installation_date`)
    },
    {
      field: 'testing_date',
      filterOperators: dateFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.testing_date`)
    }
  );

  if (!isDir) {
    columns.push({
      field: 'buying_date',
      filterOperators: dateFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.buying_date`)
    });
  }

  columns.push(
    {
      field: 'maintenance_frequency_days',
      filterOperators: numberFilterOperators,
      width: 200,
      headerName: t(`${translationPrefix}.maintenance_frequency_days`)
    },
    {
      field: 'next_preemptive_maintenance',
      filterable: false,
      sortable: false,
      width: 150,
      headerName: t(`${translationPrefix}.preemptive_maintenance_calendar`)
    }
  );

  columns.push(
    {
      field: 'decommission_date',
      filterOperators: dateFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.decommission_date`)
    },
    {
      field: 'ticketing_email',
      filterOperators,
      filterable: false,
      sortable: false,
      width: 200,
      headerName: t(`${translationPrefix}.ticketing_email`)
    },
    {
      field: 'cc_ticketing_email',
      filterOperators,
      filterable: false,
      sortable: false,
      width: 200,
      headerName: t(`${translationPrefix}.cc_ticketing_email`)
    },
    {
      field: 'assistance_contact_number',
      filterOperators,
      filterable: false,
      sortable: false,
      width: 200,
      headerName: t(`${translationPrefix}.assistance_contact_number`)
    },
    {
      field: 'assistance_supplier',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.assistance_supplier`)
    },
    {
      field: 'assistance_contract_number',
      headerName: t(`${translationPrefix}.assistance_contract_number`),
      width: 180,
      filterOperators
    },
    {
      field: 'assistance_email',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.assistance_email`)
    },
    {
      field: 'assistance_maintenance_interval',
      filterOperators: numberFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.assistance_maintenance_interval`)
    }
  );

  ['technical_file', 'user_manual', 'ce_ivd', 'maintenance_checklist_template'].forEach((file) => {
    columns.push({
      field: file,
      headerName: t(`${translationPrefix}.${file}`),
      valueFormatter: booleanValueFormatter,
      renderCell: (params: GridRenderCellParams) => {
        const equipment = params.row;
        return (
          <div>
            {viewReport && (
              <FileUploader
                onUploadSuccess={onUploadSuccess}
                service={equipmentService}
                entityId={equipment.id}
                attachmentName={file}
                filename={equipment[file]}
              />
            )}
          </div>
        );
      },
      width: 150,
      filterable: false,
      sortable: false
    });
  });

  columns.push({
    field: 'actions',
    type: 'actions',
    headerName: t('global.actions'),
    getActions: (params: GridRowParams) => {
      const actions = [];

      // Aggiunge sempre il pulsante "view document"
      actions.push(
        <GridActionsCellItem
          key="view-document"
          icon={
            <Tooltip title={t('entities.equipment.view-document')} placement="top">
              <VisibilityIcon />
            </Tooltip>
          }
          onClick={() => handleOpen && handleOpen(params.row)}
          label="view-document"
        />
      );

      // Solo se non è una directory (isDir === false)
      if (!isDir) {
        actions.unshift(
          // o push, se vuoi l’altro ordine
          <IsTenantAdministrator key="unlink">
            <ConfirmationUnlinkTableDialog
              id={params.row.id}
              handleUnlink={(id) => {
                equipmentService.purgeLinks(id).then(() => {
                  if (onUploadSuccess) onUploadSuccess();
                });
              }}
            />
          </IsTenantAdministrator>
        );
      }

      return actions;
    }
  });

  // Nascondi completamente la colonna "Associato" per non-superadmin (mai mostrarla neanche vuota)
  return (columns as GridColDef[]).filter((col) => col.field !== 'association' || isSuperAdmin);
};
